import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '../../../components/ModalSheet'
import Button from '../../../components/Button'
import React from 'react'
import { ManageUserCapacityViewModel } from './ManageUserCapacityViewModel'
import {
  Form,
  FormGroupContainer,
  FormRow,
  NumericInputContainer,
} from '../../../components/inputs'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '../../../components/RadioButtonGroup'
import { pluralize } from '../../../utils/pluralization-util'
import { SearchableUser } from 'iam/models/SearchableUser'

interface Props {
  vm: ManageUserCapacityViewModel
}

const EditUserCapacityDialog = ({ vm }: Props) => (
  <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
    <ModalSheetHeader withCloseButton />
    <ModalSheetContent>
      <ModalSheetContentBlock>
        <ModalSheetContentTitle>
          Manage {vm.capacityType === 'Global' ? 'global' : 'workspace'}{' '}
          capacity
        </ModalSheetContentTitle>

        <ModalSheetContentDescription>
          Adjust the amount of active concurrent jobs{' '}
          <b>
            {vm.selectedUser !== SearchableUser.none
              ? vm.selectedUser.displayName
              : `${vm.selectedUserIds.length} ${pluralize('user', vm.selectedUserIds.length)}`}
          </b>{' '}
          can have {vm.capacityType === 'Global' ? 'globally' : 'in'}
          <b>
            {vm.capacityType === 'Workspace' ? ` ${vm.workspace.name}` : ''}
          </b>
          .
        </ModalSheetContentDescription>
      </ModalSheetContentBlock>
      <ModalSheetContentBlock>
        <Form onSubmit={vm.submit}>
          {vm.workspace.id && vm.isPlatformAdmin && (
            <FormRow>
              <FormGroupContainer>
                <RadioButtonGroup size="big-wide">
                  <RadioButtonItem
                    dataTest="user-capacity-global-button"
                    onClick={() => vm.setCapacityType('Global')}
                    active={vm.capacityType === 'Global'}
                  >
                    Global
                  </RadioButtonItem>
                  <RadioButtonItem
                    dataTest="user-capacity-workspace-button"
                    onClick={() => vm.setCapacityType('Workspace')}
                    active={vm.capacityType === 'Workspace'}
                  >
                    Workspace
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroupContainer>
            </FormRow>
          )}

          <FormRow>
            {vm.capacityType === 'Workspace' && (
              <NumericInputContainer
                label="Workspace Amount"
                placeholder="0"
                help={`User max concurrent jobs`}
                model={vm}
                field="workspaceCapacity"
                dataTest="user-capacity-workspace-amount"
                precision={0}
              />
            )}

            {vm.capacityType === 'Global' && (
              <NumericInputContainer
                label="Global Amount"
                placeholder="0"
                help={`User max concurrent jobs`}
                model={vm}
                field="globalCapacity"
                dataTest="user-capacity-global-amount"
                precision={0}
              />
            )}
          </FormRow>
        </Form>{' '}
      </ModalSheetContentBlock>
    </ModalSheetContent>
    <ModalSheetFooter>
      <ModalSheetFooterSpacer />
      <ModalSheetFooterButtonGroup>
        <Button type="default" onClick={vm.close}>
          Close
        </Button>
        <Button
          type="primary"
          data-test="manage-user-capacity-submit-button"
          onClick={vm.submit}
          disabled={vm.submit.pending}
          loading={vm.submit.pending}
        >
          Save
        </Button>
      </ModalSheetFooterButtonGroup>
    </ModalSheetFooter>
  </ModalSheet>
)

export default observer(EditUserCapacityDialog)
